import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";
import { toast } from "sonner";
import Markdown from "@/components/Markdown";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, Send, Shield, Lock, CheckCircle2 } from "lucide-react";
import { StatusPill, PriorityPill } from "@/pages/HelpTickets";

const STATUSES = ["Open", "Awaiting customer", "Awaiting support", "Resolved", "Closed"];
const PRIORITIES = ["Low", "Normal", "High", "Urgent"];
const CATEGORIES = ["Bug", "Feature request", "Billing", "How-to question", "Other"];

/**
 * Phase B3 — single thread component shared by customer and super_admin
 * detail views. Distinguished by `isSuper` (drives internal-note toggle,
 * priority/category/assign controls).
 */
export default function HelpTicketDetail({ basePath = "/help/tickets" }) {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const { id } = useParams();
  const { formatDateTime } = useLocale();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [internalNote, setInternalNote] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/support/tickets/${id}`);
      setTicket(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const sendReply = async (e) => {
    e.preventDefault();
    if (reply.trim().length < 1) return;
    setSending(true);
    try {
      await api.post(`/support/tickets/${id}/messages`, {
        body_markdown: reply, internal_note: isSuper && internalNote,
      });
      setReply(""); setInternalNote(false);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const patch = async (changes) => {
    try {
      await api.patch(`/support/tickets/${id}`, changes);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const close = async () => {
    if (!window.confirm("Close this ticket?")) return;
    try {
      await api.post(`/support/tickets/${id}/close`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <AppShell title="Ticket"><div className="max-w-5xl mx-auto py-8 px-4 text-xs text-navy-400">Loading…</div></AppShell>;
  if (!ticket) return <AppShell title="Ticket"><div className="max-w-5xl mx-auto py-8 px-4 text-sm text-navy-500">Ticket not found.</div></AppShell>;

  const closed = ticket.status === "Closed";

  return (
    <AppShell title={ticket.subject}>
      <div className="max-w-5xl mx-auto py-8 px-4 grid grid-cols-1 md:grid-cols-[1fr_280px] gap-8">
        <div>
          <Link to={basePath} className="inline-flex items-center text-sm text-navy-500 hover:text-navy-900 mb-3" data-testid="ticket-back">
            <ChevronLeft className="w-4 h-4 mr-1" /> {isSuper ? "Ticket console" : "My tickets"}
          </Link>
          <h1 className="text-2xl font-semibold mb-2" data-testid="ticket-subject">{ticket.subject}</h1>
          <div className="flex items-center gap-2 mb-6">
            <StatusPill status={ticket.status} />
            <PriorityPill priority={ticket.priority} />
            <span className="text-xs text-navy-500">{ticket.category}</span>
          </div>

          <div className="space-y-4" data-testid="ticket-thread">
            {(ticket.messages || []).map((m) => (
              <div
                key={m.id}
                className={`eb-card p-4 ${m.internal_note ? "border-amber-300 bg-amber-50/40" : ""} ${m.author_role === "super_admin" ? "border-l-4 border-l-orange-400" : ""}`}
                data-testid={`ticket-msg-${m.id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="font-semibold text-navy-900 dark:text-white">{m.author_name}</span>
                    {m.author_role === "super_admin" && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 text-[10px] font-semibold">
                        <Shield className="w-3 h-3" /> Support
                      </span>
                    )}
                    {m.internal_note && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-200 text-amber-800 text-[10px] font-semibold" data-testid="ticket-msg-internal-flag">
                        <Lock className="w-3 h-3" /> Internal
                      </span>
                    )}
                  </div>
                  <span className="text-[11px] text-navy-400">{formatDateTime(m.at)}</span>
                </div>
                <Markdown text={m.body_markdown} />
              </div>
            ))}
          </div>

          {!closed && (
            <form onSubmit={sendReply} className="eb-card mt-6 p-4 space-y-3" data-testid="ticket-reply-form">
              <label className="block text-xs uppercase tracking-wider text-navy-500 font-semibold">Reply</label>
              <textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                rows={5}
                placeholder="Type your reply (Markdown supported)…"
                maxLength={5000}
                className="w-full border border-navy-200 rounded-md px-3 py-2 text-sm font-mono"
                data-testid="ticket-reply-body"
              />
              <div className="flex items-center justify-between">
                {isSuper ? (
                  <label className="flex items-center gap-2 text-xs text-navy-600" data-testid="ticket-reply-internal-toggle">
                    <input type="checkbox" checked={internalNote} onChange={(e) => setInternalNote(e.target.checked)} />
                    Internal note (not visible to customer)
                  </label>
                ) : <span />}
                <Button type="submit" disabled={sending} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="ticket-reply-submit">
                  <Send className="w-3.5 h-3.5 mr-1.5" /> {sending ? "Sending…" : "Send reply"}
                </Button>
              </div>
            </form>
          )}
          {closed && (
            <div className="eb-card mt-6 p-4 text-center text-sm text-navy-500" data-testid="ticket-closed-notice">
              <CheckCircle2 className="w-5 h-5 text-navy-400 mx-auto mb-2" />
              This ticket is closed. Submit a new one if you need further help.
            </div>
          )}
        </div>

        <aside className="space-y-3">
          <div className="eb-card p-4" data-testid="ticket-side-panel">
            <div className="text-[10px] uppercase tracking-wider text-navy-500 font-semibold mb-3">Ticket</div>
            <Row label="Subject">{ticket.subject}</Row>
            <Row label="Status"><StatusPill status={ticket.status} /></Row>
            <Row label="Priority"><PriorityPill priority={ticket.priority} /></Row>
            <Row label="Category">{ticket.category}</Row>
            <Row label="Opened">{formatDateTime(ticket.created_at)}</Row>
            <Row label="Last activity">{formatDateTime(ticket.last_message_at)}</Row>
          </div>
          {isSuper && (
            <div className="eb-card p-4" data-testid="ticket-side-controls">
              <div className="text-[10px] uppercase tracking-wider text-navy-500 font-semibold mb-3">Controls</div>
              <ControlSelect label="Status" value={ticket.status} options={STATUSES} onChange={(v) => patch({ status: v })} testid="ticket-control-status" />
              <ControlSelect label="Priority" value={ticket.priority} options={PRIORITIES} onChange={(v) => patch({ priority: v })} testid="ticket-control-priority" />
              <ControlSelect label="Category" value={ticket.category} options={CATEGORIES} onChange={(v) => patch({ category: v })} testid="ticket-control-category" />
            </div>
          )}
          {!closed && (
            <button onClick={close} className="w-full text-xs py-2 rounded-md border border-navy-200 text-navy-600 hover:bg-navy-50" data-testid="ticket-close-btn">
              Close ticket
            </button>
          )}
        </aside>
      </div>
    </AppShell>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between py-1.5 text-xs">
      <span className="text-navy-500">{label}</span>
      <span className="text-navy-800 dark:text-navy-100 font-medium">{children}</span>
    </div>
  );
}

function ControlSelect({ label, value, options, onChange, testid }) {
  return (
    <div className="mb-3 last:mb-0">
      <label className="block text-[10px] uppercase tracking-wider text-navy-500 font-semibold mb-1">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="text-xs" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
